/**
 * Reports failed API responses to PostHog error tracking.
 *
 * Without this, a 500 is only reported when the component that made the request
 * happens to let the error escape to Vue's errorHandler. Most `catch` blocks
 * show a toast and stop there, so the server can be failing steadily while
 * PostHog stays quiet — the client-side twin of the swallowed 500s described in
 * CLAUDE.md's Error Handling section.
 *
 * The interceptor is a safety net, not the first reporter. It rethrows the error
 * untouched and defers its own report to a macrotask, which runs after the
 * promise chain has already offered the error to the caller's `catch`. A call
 * site with better context — CourseUploader knows the user and organization —
 * therefore reports first, and `captureException` then suppresses the duplicate
 * here. Nothing reports it, and the net catches it.
 */

import { captureException, wasReported } from "./analytics";

// 4xx is the client asking for something it may not have — an expired session,
// a reviewer whose access was revoked — and is answered in the UI, not by us.
// 5xx is the server admitting it is broken, which is always worth an issue.
const isServerError = (status) => typeof status === "number" && status >= 500;

/**
 * @param {Object} axios - The axios instance to instrument.
 */
export function installApiErrorReporting(axios) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      // A cancelled request is a navigation, not a failure.
      const cancelled =
        error?.code === "ERR_CANCELED" || axios.isCancel?.(error);

      // `skipErrorReport: true` on a request config: the caller retries this
      // request and reports only if the retry fails too (presignedUpload.js).
      const retried = Boolean(error?.config?.skipErrorReport);

      if (!cancelled && !retried && isServerError(error?.response?.status)) {
        setTimeout(() => {
          if (!wasReported(error)) captureException(error);
        }, 0);
      }

      return Promise.reject(error);
    },
  );
}
